import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getCurrencySymbol } from "../../../utils/currency";

const formatCurrency = (value, symbol) => {
  const num = parseFloat(value);
  return symbol + (isNaN(num) ? "0.00" : num.toFixed(2));
};

const renderAddressLines = (addressInfo) => {
  if (!addressInfo) return [{ label: null, value: "N/A" }];
  const lines = [];
  const name = `${addressInfo?.fname || ""} ${addressInfo?.lname || ""}`.trim();
  if (name) lines.push({ label: null, value: name });
  const address1 = addressInfo?.address || addressInfo?.address1 || addressInfo?.address_1;
  const address2 = addressInfo?.address2 || addressInfo?.address_2;
  if (address1) lines.push({ label: null, value: address1 });
  if (address2) lines.push({ label: null, value: address2 });
  const postal = addressInfo?.postal || addressInfo?.postal_code || addressInfo?.postalCode;
  const cityLine = [addressInfo?.city, postal].filter(Boolean).join(", ");
  if (cityLine) lines.push({ label: null, value: cityLine });
  const stateLine = [addressInfo?.state, addressInfo?.country].filter(Boolean).join(", ");
  if (stateLine) lines.push({ label: null, value: stateLine });
  if (addressInfo?.phone) lines.push({ label: "Phone:", value: addressInfo.phone });
  if (addressInfo?.email) lines.push({ label: "Email:", value: addressInfo.email });
  return lines.length ? lines : [{ label: null, value: "N/A" }];
};

const getTransactionSummary = (txnBody) => {
  if (!txnBody || typeof txnBody !== "object") return [];
  const candidates = [
    ["Transaction ID", txnBody.id || txnBody.transaction_id],
    ["Status", txnBody.status],
    ["Payment Method", txnBody.payment_method || txnBody.payment_method_type],
  ];
  return candidates.filter(([, value]) => value !== undefined && value !== null && value !== "");
};

export const generateOrderPdf = (order) => {
  if (!order) return;

  const symbol = getCurrencySymbol(order.currency_code);
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 40;
  let cursorY = 50;

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Order Confirmation", marginX, cursorY);

  doc.setFontSize(10);
  cursorY += 25;

  doc.setFont("helvetica", "bold");
  doc.text("Order Number:", marginX, cursorY);
  doc.setFont("helvetica", "normal");
  doc.text(
    ` ${order.id ?? "N/A"}`,
    marginX + doc.getTextWidth("Order Number:"),
    cursorY
  );
  cursorY += 16;

  doc.setFont("helvetica", "bold");
  doc.text("Date:", marginX, cursorY);
  doc.setFont("helvetica", "normal");
  doc.text(
    ` ${order.date ? new Date(order.date).toLocaleDateString() : "N/A"}`,
    marginX + doc.getTextWidth("Date:"),
    cursorY
  );

  cursorY += 30;

  const billLines = renderAddressLines(order.billing_address);
  const shipLines = renderAddressLines(order.shipping_address);
  const colWidth = (pageWidth - marginX * 2 - 20) / 2;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Bill To", marginX, cursorY);
  doc.text("Ship To", marginX + colWidth + 20, cursorY);

  doc.setFontSize(9.5);
  const lineHeight = 13;

  const renderAddressColumn = (lines, x, startY) => {
    let y = startY;
    lines.forEach(({ label, value }) => {
      if (label) {
        doc.setFont("helvetica", "bold");
        doc.text(label, x, y);
        const labelWidth = doc.getTextWidth(`${label} `);
        doc.setFont("helvetica", "normal");
        const wrapped = doc.splitTextToSize(value, colWidth - labelWidth);
        doc.text(wrapped, x + labelWidth, y);
        y += wrapped.length * lineHeight;
      } else {
        doc.setFont("helvetica", "normal");
        const wrapped = doc.splitTextToSize(value, colWidth);
        doc.text(wrapped, x, y);
        y += wrapped.length * lineHeight;
      }
    });
    return y;
  };

  const billY = renderAddressColumn(billLines, marginX, cursorY + 15);
  const shipY = renderAddressColumn(shipLines, marginX + colWidth + 20, cursorY + 15);

  cursorY = Math.max(billY, shipY) + 15;

  const itemRows = [];
  (order.items || []).forEach((item) => {
    itemRows.push([
      item.product_name || "",
      item.quantity ?? "",
      formatCurrency(item.unit_price, symbol),
      formatCurrency((parseFloat(item.unit_price) || 0) * (parseFloat(item.quantity) || 0), symbol),
    ]);
    (item.modules || []).forEach((mod) => {
      itemRows.push([
        `  • ${mod.name}`,
        item.quantity ?? "",
        formatCurrency(mod.unit_price, symbol),
        formatCurrency((parseFloat(mod.unit_price) || 0) * (parseFloat(item.quantity) || 0), symbol),
      ]);
    });
  });

  autoTable(doc, {
    startY: cursorY,
    margin: { left: marginX, right: marginX },
    head: [["Product Name", "Quantity", "Unit Price", "Total"]],
    body: itemRows.length ? itemRows : [["No items", "", "", ""]],
    styles: { fontSize: 9, cellPadding: 6 },
    headStyles: { fillColor: [77, 0, 77] },
  });

  cursorY = doc.lastAutoTable.finalY + 20;

  const summaryRows = [
    ["Product Price", formatCurrency(order.product_total_price, symbol)],
  ];
  if (order.tax_amount && parseFloat(order.tax_amount) > 0) {
    summaryRows.push(["Tax", formatCurrency(order.tax_amount, symbol)]);
  }
  if (order.Shipping_fee && parseFloat(order.Shipping_fee) > 0) {
    summaryRows.push(["Shipping Fee", formatCurrency(order.Shipping_fee, symbol)]);
  }
  if (order.Insurance_fee && parseFloat(order.Insurance_fee) > 0) {
    summaryRows.push(["Insurance Fee", formatCurrency(order.Insurance_fee, symbol)]);
  }
  if (order.discounted_amount && parseFloat(order.discounted_amount) > 0) {
    summaryRows.push([
      `Discount${order.coupon_code ? ` (${order.coupon_code})` : ""}`,
      `- ${formatCurrency(order.discounted_amount, symbol)}`,
    ]);
  }
  summaryRows.push(["Total Amount", formatCurrency(order.amount, symbol)]);

  autoTable(doc, {
    startY: cursorY,
    margin: { left: pageWidth - marginX - 220, right: marginX },
    tableWidth: 220,
    body: summaryRows,
    styles: { fontSize: 9.5, cellPadding: 5 },
    theme: "plain",
    columnStyles: {
      0: { fontStyle: "normal" },
      1: { fontStyle: "bold", halign: "right" },
    },
    didParseCell: (data) => {
      if (data.row.raw[0] === "Total Amount") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fontSize = 10.5;
      }
    },
  });

  cursorY = doc.lastAutoTable.finalY + 25;

  let txnBody = order.transaction_body;
  if (typeof txnBody === "string") {
    try {
      txnBody = JSON.parse(txnBody);
    } catch (e) {
      txnBody = null;
    }
  }

  const txnSummary = getTransactionSummary(txnBody);
  if (txnSummary.length) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Payment Transaction", marginX, cursorY);
    cursorY += 15;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    txnSummary.forEach(([label, value]) => {
      doc.text(`${label}: ${value}`, marginX, cursorY);
      cursorY += 13;
    });
    cursorY += 15;
  }

  if (order.serialNumbers && order.serialNumbers.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Serial Numbers", marginX, cursorY);
    cursorY += 15;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.text(order.serialNumbers.join(", "), marginX, cursorY);
    cursorY += 25;
  }

  doc.save(`Order_Confirmation_${order.id ?? ""}.pdf`);
};

export const generateRefundReceiptPdf = (order) => {
  if (!order) return;

  const symbol = getCurrencySymbol(order.currency_code);
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const marginX = 40;
  let cursorY = 50;

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Refund Receipt", marginX, cursorY);

  cursorY += 25;
  doc.setFontSize(10);

  const infoRows = [
    ["Order Number", order.id ?? "N/A"],
    ["Order Date", order.date ? new Date(order.date).toLocaleDateString() : "N/A"],
    ["Cancellation Reason", order.cancellation_reason || "N/A"],
    ["Refund Status", order.refund_status || "N/A"],
    ["Refund ID", order.refund_id || "N/A"],
    [
      "Refund Amount",
      order.refund_amount != null && order.refund_amount !== ""
        ? formatCurrency(order.refund_amount, symbol)
        : "N/A",
    ],
  ];

  infoRows.forEach(([label, value]) => {
    doc.setFont("helvetica", "bold");
    const labelText = `${label}: `;
    doc.text(labelText, marginX, cursorY);
    const labelWidth = doc.getTextWidth(labelText);
    doc.setFont("helvetica", "normal");
    doc.text(`${value}`, marginX + labelWidth, cursorY);
    cursorY += 16;
  });

  cursorY += 20;

  const summaryRows = [];
  (order.items || []).forEach((item) => {
    summaryRows.push([
      `${item.product_name || ""} (x${item.quantity ?? 1})`,
      formatCurrency((parseFloat(item.unit_price) || 0) * (parseFloat(item.quantity) || 0), symbol),
    ]);
    (item.modules || []).forEach((mod) => {
      summaryRows.push([
        `  • ${mod.name}`,
        formatCurrency((parseFloat(mod.unit_price) || 0) * (parseFloat(item.quantity) || 0), symbol),
      ]);
    });
  });

  if (order.tax_amount && parseFloat(order.tax_amount) > 0) {
    summaryRows.push(["Tax", formatCurrency(order.tax_amount, symbol)]);
  }
  if (order.Shipping_fee && parseFloat(order.Shipping_fee) > 0) {
    summaryRows.push(["Shipping Fee", formatCurrency(order.Shipping_fee, symbol)]);
  }
  if (order.Insurance_fee && parseFloat(order.Insurance_fee) > 0) {
    summaryRows.push(["Insurance Fee", formatCurrency(order.Insurance_fee, symbol)]);
  }
  if (order.discounted_amount && parseFloat(order.discounted_amount) > 0) {
    summaryRows.push([
      `Discount${order.coupon_code ? ` (${order.coupon_code})` : ""}`,
      `- ${formatCurrency(order.discounted_amount, symbol)}`,
    ]);
  }
  summaryRows.push(["Order Total", formatCurrency(order.amount, symbol)]);

  autoTable(doc, {
    startY: cursorY,
    margin: { left: marginX, right: marginX },
    head: [["Description", "Amount"]],
    body: summaryRows,
    styles: { fontSize: 9.5, cellPadding: 6 },
    headStyles: { fillColor: [77, 0, 77] },
    columnStyles: {
      0: { fontStyle: "normal" },
      1: { fontStyle: "bold", halign: "right" },
    },
    didParseCell: (data) => {
      if (data.section === "head" && data.column.index === 1) {
        data.cell.styles.halign = "right";
      }
      if (data.section === "body" && data.row.raw[0] === "Order Total") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fontSize = 10.5;
        data.cell.styles.fillColor = [245, 243, 255];
      }
    },
  });

  doc.save(`Refund_Receipt_${order.id ?? ""}.pdf`);
};

export default generateOrderPdf;
